import { useMemo, useState } from 'react'

import { MOMENT_QUERY_FORMAT } from '@app/constants/Moment'

import type { Moment } from '@app/utils/moment'
import moment from '@app/utils/moment'
import { firstAvailableDay } from '@app/utils/timeHelper'

import type { AnnouncementsFilter } from './AnnouncementsFilter'
import { useAnnouncementsFilterManager } from './useAnnouncementsFilterManager'
import { useAnnouncementsFilterOptions } from './useAnnouncementsFilterOptions'

export const useAnnouncementsFilterController = (initialState: AnnouncementsFilter, depersonalized: boolean, now?: Moment) => {
  const minAvailableDate = useMemo(() => firstAvailableDay(now), [now])
  const [filter, setFilter] = useState(initialState)
  const { activeFiltersCount, reset, effectiveFilter, set, update } = useAnnouncementsFilterManager(filter, setFilter, minAvailableDate)

  const minDate: Moment | null = useMemo(() => toMoment(effectiveFilter.min_date) ?? null, [effectiveFilter.min_date])
  const maxDate: Moment | null = useMemo(() => toMoment(effectiveFilter.max_date) ?? null, [effectiveFilter.max_date])

  const {
    minHoursOptions,
    maxHoursOptions,
    startOptions,
    endOptions,
    sortOptions,
    childrenMinCapacityOptions,
    childrenMaxCapacityOptions,
    childrenMinAgeOptions,
    childrenMaxAgeOptions,
    regularOptions,
    statusOptions: rawStatusOptions,
  } = useAnnouncementsFilterOptions(effectiveFilter)

  const minHoursOption = minHoursOptions.find(o => o.value === effectiveFilter.min_hours)
  const maxHoursOption = maxHoursOptions.find(o => o.value === effectiveFilter.max_hours)

  const startOption = startOptions.find(o => o.value === effectiveFilter.start)
  if (startOption?.value !== effectiveFilter.start) update('start', startOption?.value ?? null)
  const endOption = endOptions.find(o => o.value === effectiveFilter.end)
  if (endOption?.value !== effectiveFilter.end) update('end', endOption?.value ?? null)

  // childrenMinCapacity
  const childrenMinCapacityOption = useMemo(
    () => childrenMinCapacityOptions.find(o => o.value === effectiveFilter.min_kids),
    [childrenMinCapacityOptions, effectiveFilter.min_kids]
  )
  if (childrenMinCapacityOption?.value !== effectiveFilter.min_kids) update('min_kids', childrenMinCapacityOption?.value ?? null)

  // childrenMaxCapacity
  const childrenMaxCapacityOption = useMemo(
    () => childrenMaxCapacityOptions.find(o => o.value === effectiveFilter.max_kids),
    [effectiveFilter.max_kids, childrenMaxCapacityOptions]
  )
  if (childrenMaxCapacityOption?.value !== effectiveFilter.max_kids) update('max_kids', childrenMaxCapacityOption?.value ?? null)

  // childrenMinAge
  const childrenMinAgeOption = useMemo(
    () => childrenMinAgeOptions.find(o => o.value === effectiveFilter.youngest_age_months),
    [childrenMinAgeOptions, effectiveFilter.youngest_age_months]
  )
  if (childrenMinAgeOption?.value !== effectiveFilter.youngest_age_months) update('youngest_age_months', childrenMinAgeOption?.value ?? null)

  // childrenMaxAge
  const childrenMaxAgeOption = useMemo(
    () => childrenMaxAgeOptions.find(o => o.value === effectiveFilter.eldest_age_months),
    [effectiveFilter.eldest_age_months, childrenMaxAgeOptions]
  )
  if (childrenMaxAgeOption?.value !== effectiveFilter.eldest_age_months) update('eldest_age_months', childrenMaxAgeOption?.value ?? null)

  // sort
  const sortOption = sortOptions.find(o => o.value.by === effectiveFilter.order_by && o.value.direction === effectiveFilter.order_dir) || sortOptions[0]
  if (sortOption.value.by !== effectiveFilter.order_by || sortOption.value.direction !== effectiveFilter.order_dir) {
    update('order_by', sortOption?.value.by ?? null)
    update('order_dir', sortOption?.value.direction ?? null)
  }

  const regularOption = regularOptions.find(o => o.value === effectiveFilter.regular)

  const statusOptions = depersonalized ? null : rawStatusOptions

  const statusOption = useMemo(
    () =>
      !statusOptions
        ? undefined
        : statusOptions.find(o => o.value === effectiveFilter.created_by) || { label: effectiveFilter.created_by, value: effectiveFilter.created_by },
    [statusOptions, effectiveFilter.created_by]
  )

  return useMemo(
    () => ({
      filter: effectiveFilter,
      rawFilter: filter,
      set,
      update,
      reset,
      minAvailableDate,
      minDate,
      maxDate,
      minHoursOption,
      minHoursOptions,
      maxHoursOption,
      maxHoursOptions,
      startOptions,
      startOption,
      endOptions,
      endOption,
      childrenMinCapacityOptions,
      childrenMinCapacityOption,
      childrenMaxCapacityOptions,
      childrenMaxCapacityOption,
      childrenMinAgeOptions,
      childrenMinAgeOption,
      childrenMaxAgeOptions,
      childrenMaxAgeOption,
      sortOptions,
      sortOption,
      regularOptions,
      regularOption,
      statusOptions,
      statusOption,
      activeFiltersCount,
    }),
    [
      effectiveFilter,
      filter,
      set,
      update,
      reset,
      minAvailableDate,
      minDate,
      maxDate,
      minHoursOption,
      minHoursOptions,
      maxHoursOption,
      maxHoursOptions,
      startOptions,
      startOption,
      endOptions,
      endOption,
      childrenMinCapacityOptions,
      childrenMinCapacityOption,
      childrenMaxCapacityOptions,
      childrenMaxCapacityOption,
      childrenMinAgeOptions,
      childrenMinAgeOption,
      childrenMaxAgeOptions,
      childrenMaxAgeOption,
      sortOptions,
      sortOption,
      regularOptions,
      regularOption,
      statusOptions,
      statusOption,
      activeFiltersCount,
    ]
  )
}

export type AnnouncementFiltersController = ReturnType<typeof useAnnouncementsFilterController>

const toMoment = (value: unknown) => {
  if (typeof value !== 'string' || !value) return undefined
  return moment(value, MOMENT_QUERY_FORMAT)
}
